import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { CONTENT_TYPE, POST } from "../config/constants";

const POST_KEY = ["posts"];

const invalidatePosts = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: POST_KEY });
  if (id) queryClient.invalidateQueries({ queryKey: ["post", id] });
};

export const useGetAllPosts = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: [...POST_KEY, params],
    queryFn: async ({ signal }) => {
      const response = await Get(POST.getAllPosts, token, params, { signal });
      return response?.data || { docs: [], totalPages: 1, totalDocs: 0 };
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetPostById = (id) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["post", id],
    queryFn: async () => {
      const response = await Get(POST.getPostById + id, token);
      return response?.data?.post;
    },
    enabled: !!token && !!id,
  });
};

export const useAddPost = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ data }) => {
      const response = await Post(POST.addPost, data, token, null, CONTENT_TYPE.FORM_DATA);
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || "Failed to create post");
    },
    onSuccess: () => invalidatePosts(queryClient),
  });
};

export const useUpdatePost = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await Post(POST.updatePost + id, data, token, null, CONTENT_TYPE.FORM_DATA);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to update post");
    },
    onSuccess: (_data, variables) => invalidatePosts(queryClient, variables?.id),
  });
};

export const useDeletePost = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Get(POST.deletePost + id, token);
      if (response?.status !== false) return response;
      throw new Error(response?.message || "Failed to delete post");
    },
    onSuccess: () => invalidatePosts(queryClient),
  });
};

export const useGetPostComments = (postId, params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["post-comments", postId, params],
    queryFn: async () => {
      const response = await Get(POST.getAllPostComments + postId, token, {
        page: params.page || 1,
        limit: params.limit || 50,
      });
      return response?.data || { docs: [], totalDocs: 0 };
    },
    enabled: !!token && !!postId,
  });
};

export const useGetPostLikes = (postId) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["post-likes", postId],
    queryFn: async () => {
      const response = await Get(POST.getPostLikes + postId, token);
      return response?.data || { docs: [], totalDocs: 0 };
    },
    enabled: !!token && !!postId,
  });
};

export const useDeleteComment = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ commentId, postId }) => {
      const response = await Get(POST.deleteComment + commentId, token);
      if (response?.status === false) {
        throw new Error(response?.message || "Failed to delete comment");
      }
      return { ...response, postId };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["post-comments", variables.postId] });
      invalidatePosts(queryClient, variables.postId);
    },
  });
};

export const useRemovePostLike = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ postId, userId }) => {
      const response = await Post(POST.removeLike + postId, { userId }, token);
      if (response?.data?.status === false) {
        throw new Error(response?.data?.message || "Failed to remove like");
      }
      return response?.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["post-likes", variables.postId] });
      invalidatePosts(queryClient, variables.postId);
    },
  });
};
